import React, { forwardRef, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Link from 'Root/Component/Link'

import styles from './index.module.css'

// Design-system Button: spec in the "Button" card of the Taylora DS.

export type ButtonVariant =
  | 'filled'
  | 'tonal'
  | 'outlined'
  | 'text'
  | 'neutral'
  | 'danger-filled'
  | 'danger-tonal'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

// Loading: the spinner shows up after 300 ms, then stays at least 500 ms
const LOADING_DELAY = 300
const LOADING_MIN = 500

interface Props
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Pick<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      'target' | 'rel' | 'download'
    > {
  variant?: ButtonVariant
  size?: ButtonSize
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  // round button with the icon only (iconLeft, or children): needs aria-label
  iconOnly?: boolean
  // renders a link with the same look
  href?: string
  // forces the Dark theme; without it the button follows its container
  dark?: boolean
  // pass it for the whole request: clicks are blocked at once, the timing
  // of the spinner is handled here. Ignored with href or disabled
  loading?: boolean
  // text shown next to the spinner (e.g. "Salvataggio…"), ignored with iconOnly
  loadingLabel?: string
  children?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (
    {
      variant = 'filled',
      size = 'md',
      iconLeft,
      iconRight,
      iconOnly = false,
      href,
      dark = false,
      disabled = false,
      loading = false,
      loadingLabel,
      type = 'button',
      className,
      style,
      children,
      onClick,
      onKeyDown,
      onKeyUp,
      onBlur,
      ...props
    },
    ref
  ) => {
    const { _ } = useLingui()

    // Pressed stays visible while Space or Enter is held down
    const [pressed, setPressed] = useState(false)

    const missingLabel =
      iconOnly && !props['aria-label'] && !props['aria-labelledby']
    useEffect(() => {
      if (missingLabel && process.env.NODE_ENV !== 'production') {
        console.warn('Button: iconOnly needs an aria-label')
      }
    }, [missingLabel])

    const isLoading = loading && !disabled && href === undefined
    const [spinnerShown, setSpinnerShown] = useState(false)
    const shownSince = useRef(0)
    const element = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
    const minWidth = useRef<number>(null)

    useEffect(() => {
      let timer: ReturnType<typeof setTimeout>
      if (isLoading && !spinnerShown) {
        timer = setTimeout(() => {
          minWidth.current = element.current?.getBoundingClientRect().width
          shownSince.current = Date.now()
          setSpinnerShown(true)
        }, LOADING_DELAY)
      } else if (!isLoading && spinnerShown) {
        timer = setTimeout(
          () => setSpinnerShown(false),
          Math.max(0, LOADING_MIN - (Date.now() - shownSince.current))
        )
      }
      return () => clearTimeout(timer)
    }, [isLoading, spinnerShown])

    const busy = isLoading || spinnerShown
    const withLoadingLabel = Boolean(loadingLabel) && !iconOnly

    const setRef = (node: HTMLButtonElement | HTMLAnchorElement) => {
      element.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    const common = {
      ...props,
      className: classNames(
        styles.button,
        styles[variant],
        styles[size],
        {
          [styles['icon-only']]: iconOnly,
          [styles.pressed]: pressed && !busy,
          [styles['loading-center']]: spinnerShown && !withLoadingLabel,
          [styles['loading-label']]: spinnerShown && withLoadingLabel,
        },
        className
      ),
      style:
        spinnerShown && withLoadingLabel
          ? { ...style, minWidth: minWidth.current }
          : style,
      'data-theme': dark ? 'dark' : undefined,
      onClick: (event: React.MouseEvent<any>) => {
        // a busy submit button must not send the form again
        if (busy) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      },
      onKeyDown: (event: React.KeyboardEvent<any>) => {
        onKeyDown?.(event)
        if (!disabled && (event.key === ' ' || event.key === 'Enter')) {
          setPressed(true)
        }
      },
      onKeyUp: (event: React.KeyboardEvent<any>) => {
        setPressed(false)
        onKeyUp?.(event)
      },
      onBlur: (event: React.FocusEvent<any>) => {
        setPressed(false)
        onBlur?.(event)
      },
    }

    // busy uses aria-disabled, not disabled, so the focus stays on the button
    if (busy) {
      common['aria-busy'] = true
      common['aria-disabled'] = true
    }
    // with the spinner in the centre the accessible name says it's loading;
    // with loadingLabel it's the visible text
    if (spinnerShown && !withLoadingLabel) {
      common['aria-label'] = _(msg`Caricamento in corso`)
      delete common['aria-labelledby']
    }

    const icon = (node: React.ReactNode) =>
      node ? (
        <span className={styles.icon} aria-hidden="true">
          {node}
        </span>
      ) : null

    const spinner = spinnerShown ? (
      <span className={styles.spinner} aria-hidden="true" />
    ) : null

    let content: React.ReactNode
    if (spinnerShown && withLoadingLabel) {
      content = (
        <>
          {spinner}
          <span>{loadingLabel}</span>
        </>
      )
    } else if (iconOnly) {
      content = (
        <>
          {spinner}
          {icon(iconLeft ?? children)}
        </>
      )
    } else {
      content = (
        <>
          {spinner}
          {icon(iconLeft)}
          <span>{children}</span>
          {icon(iconRight)}
        </>
      )
    }

    if (href !== undefined) {
      // a disabled link loses its href, leaves the tab order and ignores
      // clicks (unlike a disabled <button>, an <a> still fires onClick)
      return disabled ? (
        <a
          {...(common as any)}
          ref={setRef}
          aria-disabled="true"
          tabIndex={-1}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {content}
        </a>
      ) : (
        <Link {...(common as any)} ref={setRef} to={href}>
          {content}
        </Link>
      )
    }

    return (
      <button {...common} ref={setRef} type={type} disabled={disabled}>
        {content}
      </button>
    )
  }
)
Button.displayName = 'Button'

export default Button
